/**
 * Utility functions for formatting numbers, prices, and dates
 */

/**
 * Formats a number as a German price with thousands separator and decimal comma
 * @param price - The price to format
 * @param includeCurrency - Whether to include the € symbol (default: true)
 * @returns Formatted price string (e.g., "12.990,00 €")
 */
export const formatPrice = (price: number, includeCurrency: boolean = true): string => {
  const formatted = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);

  return includeCurrency ? `${formatted} €` : formatted;
};

/**
 * Formats a number as German currency with EUR symbol
 * @param price - The price to format
 * @returns Formatted price with currency symbol (e.g., "12.990,00 €")
 */
export const formatCurrency = (price: number): string => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(price);
};

/**
 * Formats a number with German thousands separator and decimal comma (no currency)
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted number string (e.g., "12.990,00")
 */
export const formatNumber = (value: number, decimals: number = 2): string => {
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};
